import { Component, ElementRef } from '@angular/core';
import * as d3 from 'd3';
import { Utils } from '../../shared/utils';
import { TooltipOptions } from '../../tooltip/tooltip.component';
import { ChartOptions, MproChart } from '../chart.model';

export class GroupedBarChartDataModel {
  constructor(public group: string, public bars: GroupedBarDataModel[]) {}
}

export class GroupedBarDataModel {
  constructor(
    public key: string,
    public value: number,
    public tooltip?: TooltipOptions
  ) {}
}

export class GroupedBarChartOptions extends ChartOptions {
  dataAxisStep: number;
  tickFormat?: (tick: any) => string;
}

@Component({
  selector: 'mpro-grouped-bar-chart',
  templateUrl: './grouped-bar-chart.component.html',
  styleUrls: ['./grouped-bar-chart.component.scss'],
})
export class GroupedBarChartComponent extends MproChart<
  GroupedBarChartDataModel[],
  GroupedBarChartOptions
> {
  protected margin = { top: 10, right: 10, bottom: 10, left: 30 };

  constructor(protected el: ElementRef) {
    super(el);
  }

  protected hasData(): boolean {
    return !!(this.data && this.data.length);
  }

  protected drawChart() {
    const groups = this.data.map((dataEntry) => dataEntry.group);
    const subgroups = this.data[0].bars.map((d) => d.key);
    const chartColorPalette: any = d3
      .scaleOrdinal()
      .domain(subgroups)
      .range(this.options.colors.map((color) => Utils.getColorHex(color)));

    const maxDataValue = this.getBarsMaxValue();
    const dataAxisStepGap = Math.ceil(maxDataValue / this.options.dataAxisStep);

    // Add X axis
    const x = d3.scaleBand().domain(groups).range([0, this.width]).padding(0.3);

    // Add ticks
    this.svg
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(d3.axisBottom(x).tickSize(0));

    // Add Y axis
    const y = d3
      .scaleLinear()
      .domain([0, dataAxisStepGap * this.options.dataAxisStep])
      .range([this.height, 0]);

    // Add ticks
    this.svg
      .append('g')
      .call(
        d3
          .axisLeft(y)
          .ticks(this.options.dataAxisStep)
          .tickFormat((d: any) =>
            this.options.tickFormat ? this.options.tickFormat(d) : d
          )
          // span ticks across the whole chart width
          .tickSizeInner(-this.width)
          .tickSizeOuter(0)
          .tickPadding(10)
      )
      .call((g: any) => g.select('.domain').remove());

    // Another scale for subgroup position
    const xSubgroup: any = d3
      .scaleBand()
      .domain(subgroups)
      .range([0, x.bandwidth()]) // fixed width of bars
      .padding(0.2);

    // Show the bars
    this.svg
      .append('g')
      .selectAll('g')
      // Enter in data = loop group per group
      .data(this.data)
      .enter()
      .append('g')
      .attr('class', (d: any) => `group ${d.group}`)
      .attr('transform', (d: any) => `translate(${x(d.group)}, 0)`)
      .selectAll('rect')
      .data((d: any) => d.bars)
      .enter()
      .append('rect')
      .attr('class', (d: any) => `subgroup ${d.key}`)
      .attr('x', (d: any) => xSubgroup(d.key))
      .attr('y', (d: any) => y(d.value))
      .attr('width', xSubgroup.bandwidth())
      .attr('height', (d: any) => this.height - y(d.value))
      .attr('fill', (d: any) => chartColorPalette(d.key))
      // rounded corners
      .attr('rx', 3)
      .attr('ry', 3);
  }

  private getBarsMaxValue(): number {
    return this.data.reduce((max, dataEntry) => {
      const barsMax = Math.max.apply(
        Math,
        dataEntry.bars.map((bar) => bar.value)
      );
      if (barsMax > max) {
        max = barsMax;
      }

      return max;
    }, 0);
  }
}
